<template>
  <div class="roulette-wrapper">
    <!-- ═══ LOBBY ═══ -->
    <div v-if="phase === 'lobby'" class="roulette-card">
      <div class="text-center mb-6">
        <div class="text-4xl mb-2">🎰</div>
        <h2 class="text-2xl font-bold text-white">{{ t('gamification.roulette.title') }}</h2>
        <p class="text-purple-200 mt-1">{{ t('gamification.roulette.subtitle') }}</p>
      </div>

      <!-- Mode Toggle -->
      <div v-if="!hasJoined" class="flex justify-center gap-2 mb-4">
        <button
          @click="avatarMode = 'avatar'"
          :class="['px-4 py-2 rounded-xl text-sm font-medium transition-all', avatarMode === 'avatar' ? 'bg-white text-purple-900' : 'bg-white/10 text-white']"
        >Ohne Kamera</button>
        <button
          @click="avatarMode = 'camera'"
          :class="['px-4 py-2 rounded-xl text-sm font-medium transition-all', avatarMode === 'camera' ? 'bg-white text-purple-900' : 'bg-white/10 text-white']"
        >Mit Kamera 📹</button>
      </div>

      <!-- Camera Preview -->
      <div v-if="avatarMode === 'camera' && !hasJoined" class="mb-4 flex justify-center">
        <div class="w-24 h-24 rounded-full overflow-hidden border-4 border-white/30">
          <video ref="lobbyPreviewVideo" autoplay muted playsinline class="w-full h-full object-cover mirror"></video>
        </div>
      </div>

      <!-- Avatar Color -->
      <div v-if="avatarMode === 'avatar' && !hasJoined" class="mb-4">
        <div class="flex justify-center gap-2 flex-wrap">
          <button
            v-for="color in avatarColors" :key="color"
            @click="selectedColor = color"
            :class="['w-10 h-10 rounded-full transition-all border-2', selectedColor === color ? 'border-white scale-125' : 'border-transparent']"
            :style="{ backgroundColor: color }"
          ></button>
        </div>
      </div>

      <!-- Name -->
      <div v-if="!hasJoined" class="mb-4">
        <input v-model="displayName" placeholder="Dein Name"
          class="w-full px-4 py-3 rounded-xl bg-white/10 text-white placeholder-purple-300 border border-white/20 focus:border-white/50 focus:outline-none text-center"
          maxlength="20" />
      </div>

      <p v-if="joinError" class="text-red-400 text-xs text-center mb-2">{{ joinError }}</p>

      <button v-if="!hasJoined" @click="joinGame" :disabled="!displayName.trim()"
        class="w-full py-3 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-bold text-lg transition-all hover:scale-[1.02] disabled:opacity-50">
        Beitreten
      </button>

      <!-- Video Grid in Lobby (if any participant has camera) -->
      <div v-if="hasJoined && showVideoGrid" class="video-grid-lobby mt-4" :class="gridClass">
        <div v-for="p in participants" :key="p.id" class="video-cell-lobby">
          <video v-if="isMe(p)" data-local="true" :ref="el => setLocalVideo(el)" autoplay muted playsinline class="cell-video mirror"></video>
          <template v-else>
            <video v-show="rtc.remoteStreams.value[p.sessionIdentifier]" :data-peer-id="p.sessionIdentifier" :ref="el => setRemoteVideo(el, p.sessionIdentifier)" autoplay muted playsinline class="cell-video mirror"></video>
            <div v-show="!rtc.remoteStreams.value[p.sessionIdentifier]" class="cell-avatar" :style="{ backgroundColor: p.avatarColor || '#6366f1' }">
              <span class="text-2xl font-bold">{{ initial(p) }}</span>
              <div class="connecting-pulse"></div>
            </div>
          </template>
          <span class="cell-name">{{ p.displayName }}{{ isMe(p) ? ' (Du)' : '' }}</span>
        </div>
      </div>

      <!-- Text participant list (non-camera) -->
      <div v-if="hasJoined && !showVideoGrid" class="mt-6">
        <p class="text-sm text-purple-300 mb-2">Teilnehmer ({{ participants.length }})</p>
        <div class="space-y-2">
          <div v-for="p in participants" :key="p.id" class="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-2">
            <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
              :style="{ backgroundColor: p.avatarColor || '#6366f1' }">{{ initial(p) }}</div>
            <span class="flex-1 text-white">{{ p.displayName }}</span>
            <span v-if="p.isHost" class="text-xs bg-yellow-500/30 text-yellow-300 px-2 py-0.5 rounded-full">Host</span>
          </div>
        </div>
      </div>

      <!-- Non-camera participant count -->
      <div v-if="!hasJoined" class="mt-6">
        <p class="text-sm text-purple-300 mb-2">Teilnehmer ({{ participants.length }})</p>
        <div class="space-y-2">
          <div v-for="p in participants" :key="p.id" class="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-2">
            <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
              :style="{ backgroundColor: p.avatarColor || '#6366f1' }">{{ initial(p) }}</div>
            <span class="flex-1 text-white">{{ p.displayName }}</span>
          </div>
        </div>
      </div>

      <button v-if="isHostPlayer && hasJoined && participants.length >= 2" @click="startSpin"
        class="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 text-white font-bold text-lg animate-pulse hover:animate-none transition-all hover:scale-[1.02]">
        🎰 DREHEN!
      </button>

      <p v-if="hasJoined && participants.length < 2" class="text-center text-purple-300 mt-4 text-sm">
        {{ t('gamification.roulette.minPlayers') }}
      </p>
    </div>

    <!-- ═══ SPINNING (Camera Grid) ═══ -->
    <div v-if="phase === 'spinning' && showVideoGrid" class="spin-fullscreen">
      <div class="spin-header">
        <h2 class="text-xl font-bold text-white animate-pulse">🎯 Wer wird es treffen?</h2>
      </div>

      <div class="video-grid-spin" :class="gridClass">
        <div v-for="(p, idx) in participants" :key="p.id"
          :class="['video-cell-spin', {
            'cell-scanning': spinIdx === idx && !spinLocked,
            'cell-locked': spinIdx === idx && spinLocked,
            'cell-dimmed': spinIdx !== idx,
          }]">
          <video v-if="isMe(p)" data-local="true" :ref="el => setLocalVideo(el)" autoplay muted playsinline class="cell-video mirror"></video>
          <template v-else>
            <video v-show="rtc.remoteStreams.value[p.sessionIdentifier]" :data-peer-id="p.sessionIdentifier" :ref="el => setRemoteVideo(el, p.sessionIdentifier)" autoplay muted playsinline class="cell-video mirror"></video>
            <div v-show="!rtc.remoteStreams.value[p.sessionIdentifier]" class="cell-avatar" :style="{ backgroundColor: p.avatarColor || '#6366f1' }">
              <span class="text-3xl font-bold">{{ initial(p) }}</span>
            </div>
          </template>

          <div v-if="spinIdx === idx" class="cell-crosshair" :class="{ locked: spinLocked }">
            <div class="xhair-ring"></div>
            <div class="xhair-h"></div>
            <div class="xhair-v"></div>
            <div class="xhair-dot"></div>
          </div>

          <span class="cell-name-spin" :class="{ 'name-active': spinIdx === idx }">{{ p.displayName }}</span>
        </div>
      </div>
    </div>

    <!-- ═══ SPINNING (Classic, no camera) ═══ -->
    <div v-if="phase === 'spinning' && !showVideoGrid" class="roulette-card">
      <div class="text-center mb-6">
        <h2 class="text-2xl font-bold text-white animate-pulse">🎰 Roulette dreht...</h2>
      </div>
      <div class="relative h-64 overflow-hidden rounded-2xl bg-black/30">
        <div ref="spinnerContainer" class="absolute inset-0 flex flex-col items-center" :style="classicSpinStyle">
          <div v-for="(p, i) in classicSequence" :key="i"
            :class="['w-full py-6 flex items-center justify-center gap-4 transition-all', classicHighlight === i ? 'bg-white/20 scale-105' : '']">
            <div class="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg"
              :style="{ backgroundColor: p.avatarColor || '#6366f1' }">{{ initial(p) }}</div>
            <span class="text-xl font-bold text-white">{{ p.displayName }}</span>
          </div>
        </div>
        <div class="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.8)] z-10"></div>
      </div>
    </div>

    <!-- ═══ RESULT (Camera Grid) ═══ -->
    <div v-if="phase === 'result' && showVideoGrid" class="result-fullscreen">
      <div class="video-grid-result" :class="gridClass">
        <div v-for="p in participants" :key="p.id"
          :class="['video-cell-result', isLoser(p) ? 'cell-loser' : 'cell-winner']">
          <video v-if="isMe(p)" data-local="true" :ref="el => setLocalVideo(el)" autoplay muted playsinline class="cell-video mirror"></video>
          <template v-else>
            <video v-show="rtc.remoteStreams.value[p.sessionIdentifier]" :data-peer-id="p.sessionIdentifier" :ref="el => setRemoteVideo(el, p.sessionIdentifier)" autoplay muted playsinline class="cell-video mirror"></video>
            <div v-show="!rtc.remoteStreams.value[p.sessionIdentifier]" class="cell-avatar" :style="{ backgroundColor: p.avatarColor || '#6366f1' }">
              <span class="text-3xl font-bold">{{ initial(p) }}</span>
            </div>
          </template>

          <!-- Loser: blood/headshot -->
          <div v-if="isLoser(p)" class="loser-overlay">
            <div class="blood-splat b1"></div>
            <div class="blood-splat b2"></div>
            <div class="blood-splat b3"></div>
            <div class="red-vignette"></div>
            <div class="headshot-stamp">
              <span class="hs-icon">💀</span>
              <span class="hs-text">HEADSHOT</span>
            </div>
          </div>

          <!-- Winner: shield -->
          <div v-if="!isLoser(p)" class="winner-overlay">
            <div class="shield-glow"></div>
          </div>

          <span :class="['cell-name-result', isLoser(p) ? 'name-loser' : 'name-winner']">
            {{ p.displayName }} {{ isLoser(p) ? '💀 ZAHLT!' : '✓ SAFE' }}
          </span>
        </div>
      </div>

      <div class="result-actions">
        <button v-if="iAmLoser" @click="$emit('go-to-checkout')"
          class="w-full py-4 rounded-xl bg-red-600 text-white font-bold text-lg hover:bg-red-700 transition-all">
          {{ t('gamification.roulette.toCheckout') }} 💸
        </button>
        <button v-else @click="$emit('close')"
          class="w-full py-4 rounded-xl bg-green-600 text-white font-bold text-lg hover:bg-green-700 transition-all">
          {{ t('gamification.common.close') }}
        </button>
      </div>
    </div>

    <!-- ═══ RESULT (Classic, no camera) ═══ -->
    <div v-if="phase === 'result' && !showVideoGrid" class="text-center">
      <div v-if="iAmLoser" class="animate-result-loser">
        <div class="bg-red-600 rounded-3xl p-8 shadow-2xl border-4 border-red-400">
          <div class="text-6xl mb-4">💥</div>
          <h2 class="text-4xl font-black text-white mb-2 animate-bounce">{{ t('gamification.roulette.busted') }}</h2>
          <p class="text-xl text-red-100">{{ t('gamification.roulette.youPay') }}</p>
          <button @click="$emit('go-to-checkout')"
            class="mt-6 w-full py-4 rounded-xl bg-white text-red-600 font-bold text-lg hover:bg-gray-100 transition-all">{{ t('gamification.roulette.toCheckout') }} 💸</button>
        </div>
      </div>
      <div v-else class="animate-result-winner">
        <div class="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-8 shadow-2xl border-4 border-green-300">
          <div class="text-6xl mb-4">🎉</div>
          <h2 class="text-4xl font-black text-white mb-2">SAFE!</h2>
          <p class="text-xl text-green-100">{{ t('gamification.roulette.youWin') }}</p>
          <p class="text-green-200 mt-2">{{ loserDisplayName }} {{ t('gamification.roulette.someoneElsePays', { name: '' }) }} 😈</p>
          <button @click="$emit('close')" class="mt-6 w-full py-4 rounded-xl bg-white text-green-600 font-bold text-lg hover:bg-gray-100 transition-all">
            {{ t('gamification.common.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGamificationStore } from '@/stores/gamification'
import { gamificationService } from '@/services/api/gamificationService'
import { useWebRTC } from '@/composables/useWebRTC'

const props = defineProps({
  visible: { type: Boolean, default: true },
  orderSessionId: { type: [Number, String], default: null },
  sessionIdentifier: { type: String, default: '' },
  tableId: { type: [Number, String], default: null },
  isHost: { type: Boolean, default: true },
  gameId: { type: [Number, String], default: null },
})

defineEmits(['close', 'go-to-checkout'])
const { t } = useI18n()
const store = useGamificationStore()
const rtc = useWebRTC()

const phase = ref('lobby')
const avatarMode = ref('avatar')
const useCameraMode = ref(false)
const displayName = ref('')
const selectedColor = ref('#6366f1')
const hasJoined = ref(false)
const joinError = ref('')
const loserId = ref(null)
const lobbyPreviewVideo = ref(null)

// Classic spin state
const classicSpinStyle = ref({})
const classicHighlight = ref(-1)
const classicSequence = ref([])

// Camera spin state
const spinIdx = ref(-1)
const spinLocked = ref(false)

const avatarColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#6366f1', '#a855f7', '#ec4899']

let participantPollInterval = null
const connectedPeers = new Set()
let peerCheckInterval = null

// ─── Computed ─────────────────────────────────────────

const participants = computed(() => {
  if (!store.rouletteGame) return []
  return (store.rouletteGame.participants || []).map(p => ({
    ...p,
    avatarColor: p.avatarColor || p.avatar_color,
    displayName: p.displayName || p.display_name,
    isHost: p.isHost || p.is_host,
    sessionIdentifier: p.sessionIdentifier || p.session_identifier,
    avatarType: p.avatarType || p.avatar_type,
  }))
})

const isHostPlayer = computed(() => props.isHost)

const myIdentifier = computed(() =>
  props.sessionIdentifier || localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''
)

const iAmLoser = computed(() => {
  if (!loserId.value) return false
  const loser = participants.value.find(p => p.id === loserId.value)
  return loser && loser.sessionIdentifier === myIdentifier.value
})

const loserDisplayName = computed(() => {
  if (!loserId.value) return 'Jemand'
  const loser = participants.value.find(p => p.id === loserId.value)
  return loser ? loser.displayName : 'Jemand'
})

const gridClass = computed(() => {
  const count = participants.value.length
  if (count <= 2) return 'grid-2'
  if (count <= 4) return 'grid-4'
  return 'grid-6'
})

// ─── Helpers ──────────────────────────────────────────

function initial(p) {
  return ((p.displayName || '?')[0] || '?').toUpperCase()
}

function isMe(p) {
  return p.sessionIdentifier === myIdentifier.value
}

function isLoser(p) {
  return p.id === loserId.value
}

function setLocalVideo(el) {
  if (el && rtc.localStream.value) {
    el.srcObject = rtc.localStream.value
  }
}

function setRemoteVideo(el, peerId) {
  if (!el) return
  const stream = rtc.remoteStreams.value[peerId]
  if (stream && el.srcObject !== stream) {
    el.srcObject = stream
    el.muted = true
    el.play().catch(() => {})
  }
}

function triggerHaptic() {
  if ('vibrate' in navigator) navigator.vibrate([200, 100, 200, 100, 400])
}

const showVideoGrid = computed(() =>
  participants.value.some(p => p.avatarType === 'camera') || rtc.localStream.value != null
)

watch(() => rtc.remoteStreams.value, async () => {
  await nextTick()
  document.querySelectorAll('video[data-peer-id]').forEach(el => {
    const peerId = el.getAttribute('data-peer-id')
    const stream = rtc.remoteStreams.value[peerId]
    if (stream && el.srcObject !== stream) {
      el.srcObject = stream
      el.muted = true
      el.play().catch(() => {})
    }
  })
}, { deep: true, immediate: true })

watch(() => rtc.localStream.value, async () => {
  await nextTick()
  document.querySelectorAll('video[data-local]').forEach(el => {
    if (rtc.localStream.value && el.srcObject !== rtc.localStream.value) {
      el.srcObject = rtc.localStream.value
    }
  })
})

// ─── WebRTC connection management ─────────────────────

function connectToNewPeers() {
  if (!store.rouletteGame?.id) return
  for (const p of participants.value) {
    if (p.sessionIdentifier === myIdentifier.value) continue
    if (!p.sessionIdentifier) continue
    rtc.connectToPeer(p.sessionIdentifier)
    connectedPeers.add(p.sessionIdentifier)
  }
}

function startPeerCheck() {
  stopPeerCheck()
  peerCheckInterval = setInterval(() => {
    connectToNewPeers()
  }, 3000)
}

function stopPeerCheck() {
  if (peerCheckInterval) { clearInterval(peerCheckInterval); peerCheckInterval = null }
}

async function ensureWebRTC() {
  if (!store.rouletteGame?.id || !myIdentifier.value) return
  if (!rtc.isPolling()) {
    rtc.configure(store.rouletteGame.id, myIdentifier.value)
    rtc.startPolling()
    connectToNewPeers()
    startPeerCheck()
  }
}

// ─── Polling ──────────────────────────────────────────

function startPolling() {
  stopPolling()
  participantPollInterval = setInterval(async () => {
    if (!store.rouletteGame?.id) return
    try {
      const { data } = await gamificationService.getRouletteGame(store.rouletteGame.id)
      if (data) {
        store.rouletteGame = data
        if (hasJoined.value) {
          ensureWebRTC()
          connectToNewPeers()
        }
        if (data.status === 'spinning' && phase.value === 'lobby') startSpinAnimation()
        if (data.status === 'finished' && phase.value !== 'result') {
          loserId.value = data.loserParticipantId || data.loser_participant_id
          showResult()
        }
      }
    } catch { /* retry */ }
  }, 2000)
}

function stopPolling() {
  if (participantPollInterval) { clearInterval(participantPollInterval); participantPollInterval = null }
}

// ─── Lifecycle ────────────────────────────────────────

onMounted(async () => {
  if (props.gameId && !store.rouletteGame) {
    try {
      const { data } = await gamificationService.getRouletteGame(props.gameId)
      if (data) store.rouletteGame = data
    } catch { /* ignore */ }
  }

  if (!store.rouletteGame && props.tableId) {
    try {
      const { data } = await gamificationService.getActiveRouletteForTable(props.tableId)
      if (data && data.id && ['waiting', 'spinning'].includes(data.status)) {
        store.rouletteGame = data
      }
    } catch { /* ignore */ }
  }

  startPolling()
})

// ─── Avatar mode watch ────────────────────────────────

watch(() => avatarMode.value, async (mode) => {
  if (mode === 'camera') {
    const ok = await rtc.initCamera()
    if (ok) {
      await nextTick()
      if (lobbyPreviewVideo.value) {
        lobbyPreviewVideo.value.srcObject = rtc.localStream.value
      }
    } else {
      avatarMode.value = 'avatar'
    }
  }
})

// ─── Game state watch ─────────────────────────────────

watch(() => store.rouletteGame, (game) => {
  if (!game) return
  if (game.status === 'spinning' && phase.value === 'lobby') startSpinAnimation()
  if (game.status === 'finished' && phase.value !== 'result') {
    loserId.value = game.loserParticipantId || game.loser_participant_id
    showResult()
  }
}, { deep: true })

// ─── Join ─────────────────────────────────────────────

async function joinGame() {
  if (!displayName.value.trim()) return
  joinError.value = ''

  const sid = props.orderSessionId || parseInt(localStorage.getItem('sessionNumericId')) || null
  const identifier = myIdentifier.value
  if (!identifier) { joinError.value = 'Session-Fehler. Bitte QR-Code erneut scannen.'; return }

  if (!store.rouletteGame && props.tableId) {
    try {
      const { data } = await gamificationService.getActiveRouletteForTable(props.tableId)
      if (data && data.id && ['waiting', 'spinning'].includes(data.status)) {
        store.rouletteGame = data
      }
    } catch { /* ignore */ }
  }

  try {
    if (store.rouletteGame) {
      const alreadyJoined = (store.rouletteGame.participants || []).some(
        p => (p.sessionIdentifier || p.session_identifier) === identifier
      )
      if (!alreadyJoined) {
        await store.joinRouletteGame(store.rouletteGame.id, identifier, displayName.value.trim(), avatarMode.value, selectedColor.value, null)
      }
    } else {
      if (!sid) { joinError.value = 'Du musst zuerst etwas bestellen.'; return }
      await store.createRouletteGame(sid, identifier, displayName.value.trim(), avatarMode.value, selectedColor.value, null)
    }

    hasJoined.value = true
    useCameraMode.value = avatarMode.value === 'camera'

    if (!rtc.localStream.value) {
      const camOk = await rtc.initCamera()
      if (!camOk && useCameraMode.value) {
        console.warn('[Roulette] Camera failed, falling back to avatar mode')
        useCameraMode.value = false
      }
    }

    if (store.rouletteGame?.id) {
      rtc.configure(store.rouletteGame.id, identifier)
      rtc.startPolling()
      await nextTick()
      connectToNewPeers()
      startPeerCheck()
    }
  } catch (e) {
    console.error('Join failed:', e)
    joinError.value = e.response?.status === 422
      ? 'Session fehlt. Bitte QR-Code erneut scannen.'
      : 'Beitritt fehlgeschlagen.'
  }
}

// ─── Spin ─────────────────────────────────────────────

async function startSpin() {
  if (!store.rouletteGame) return
  await store.spinRoulette(store.rouletteGame.id, myIdentifier.value)
}

function startSpinAnimation() {
  phase.value = 'spinning'
  const players = [...participants.value]
  if (players.length < 2) return

  const gameLoser = store.rouletteGame
    ? (store.rouletteGame.loserParticipantId || store.rouletteGame.loser_participant_id)
    : null
  loserId.value = gameLoser

  if (showVideoGrid.value) {
    runCameraSpinAnimation(players, gameLoser)
  } else {
    runClassicSpinAnimation(players, gameLoser)
  }
}

function runCameraSpinAnimation(players, gameLoser) {
  spinLocked.value = false
  spinIdx.value = 0

  const totalCycles = 28
  const baseDuration = 100
  let step = 0
  const loserIdx = players.findIndex(p => p.id === gameLoser)

  function tick() {
    spinIdx.value = step % players.length
    step++

    if (step < totalCycles) {
      const progress = step / totalCycles
      const delay = baseDuration + Math.pow(progress, 2.5) * 700
      setTimeout(tick, delay)
    } else {
      if (loserIdx >= 0) spinIdx.value = loserIdx
      spinLocked.value = true
      triggerHaptic()

      setTimeout(async () => {
        if (store.rouletteGame) {
          try { await store.finishRoulette(store.rouletteGame.id) } catch { /* ok */ }
          showResult()
        }
      }, 1800)
    }
  }

  setTimeout(tick, 600)
}

function runClassicSpinAnimation(players, gameLoser) {
  const sequence = []
  for (let i = 0; i < 40; i++) sequence.push(players[i % players.length])
  const loserP = players.find(p => p.id === gameLoser)
  if (loserP) sequence[sequence.length - 3] = loserP
  classicSequence.value = sequence

  let idx = 0
  const totalSteps = sequence.length - 3
  const baseDuration = 80
  let timeout = baseDuration

  function step() {
    classicHighlight.value = idx
    idx++
    if (idx < totalSteps) {
      const progress = idx / totalSteps
      timeout = baseDuration + Math.pow(progress, 3) * 800
      classicSpinStyle.value = {
        transform: `translateY(-${idx * 88}px)`,
        transition: `transform ${timeout}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
      }
      setTimeout(step, timeout)
    } else {
      setTimeout(async () => {
        triggerHaptic()
        if (store.rouletteGame) {
          try { await store.finishRoulette(store.rouletteGame.id) } catch { /* ok */ }
          showResult()
        }
      }, 1000)
    }
  }
  setTimeout(step, 500)
}

async function showResult() {
  phase.value = 'result'
  if (iAmLoser.value) {
    if ('vibrate' in navigator) navigator.vibrate([500, 200, 500, 200, 1000])
  }
  await nextTick()
}

// ─── Cleanup ──────────────────────────────────────────

onUnmounted(() => {
  stopPolling()
  stopPeerCheck()
  rtc.cleanup()
})
</script>

<style scoped>
.roulette-wrapper { width: 100%; }
.roulette-card {
  background: linear-gradient(135deg, #581c87, #312e81);
  border-radius: 1.5rem;
  padding: 1.5rem;
  color: white;
  box-shadow: 0 25px 50px rgba(0,0,0,0.3);
}
.mirror { transform: scaleX(-1); }

/* ─── Video Grid Layouts ─── */
.grid-2 { grid-template-columns: 1fr 1fr; }
.grid-4 { grid-template-columns: 1fr 1fr; }
.grid-6 { grid-template-columns: 1fr 1fr 1fr; }

/* ─── Lobby Grid ─── */
.video-grid-lobby {
  display: grid;
  gap: 0.5rem;
}
.video-cell-lobby {
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 1rem;
  overflow: hidden;
  background: #0a0a14;
  border: 2px solid rgba(255,255,255,0.1);
}
.cell-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cell-avatar {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
}
.cell-name {
  position: absolute;
  bottom: 4px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 1px 4px rgba(0,0,0,0.8);
  padding: 2px 4px;
  background: linear-gradient(transparent, rgba(0,0,0,0.6));
}
.connecting-pulse {
  position: absolute;
  bottom: 30px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fbbf24;
  animation: cPulse 1.5s infinite;
}
@keyframes cPulse {
  0%,100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.5); }
}

/* ─── Spinning Grid ─── */
.spin-fullscreen {
  width: 100%;
  color: white;
}
.spin-header {
  text-align: center;
  margin-bottom: 0.75rem;
}
.video-grid-spin {
  display: grid;
  gap: 0.5rem;
}
.video-cell-spin {
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 1rem;
  overflow: hidden;
  background: #0a0a14;
  border: 3px solid transparent;
  transition: all 0.15s ease;
}
.cell-scanning {
  border-color: #ef4444;
  box-shadow: 0 0 25px rgba(239,68,68,0.5);
  transform: scale(1.03);
  z-index: 5;
}
.cell-locked {
  border-color: #ff0000;
  box-shadow: 0 0 40px rgba(255,0,0,0.7), inset 0 0 20px rgba(255,0,0,0.15);
  transform: scale(1.06);
  z-index: 5;
  animation: lockedPulse 0.5s ease-in-out infinite;
}
.cell-dimmed {
  opacity: 0.5;
  filter: brightness(0.6);
}

@keyframes lockedPulse {
  0%,100% { box-shadow: 0 0 30px rgba(255,0,0,0.5); }
  50% { box-shadow: 0 0 50px rgba(255,0,0,0.8); }
}

/* Crosshair on cell */
.cell-crosshair {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
}
.xhair-ring {
  width: 60%;
  aspect-ratio: 1;
  border: 2px solid rgba(255,50,50,0.6);
  border-radius: 50%;
  animation: xhScan 1.2s ease-in-out infinite;
}
.locked .xhair-ring {
  border-color: #ff0000;
  box-shadow: 0 0 20px rgba(255,0,0,0.5);
  animation: xhLock 0.3s ease-out forwards;
}
.xhair-h, .xhair-v {
  position: absolute;
  background: rgba(255,50,50,0.4);
}
.xhair-h { width: 100%; height: 1px; top: 50%; }
.xhair-v { height: 100%; width: 1px; left: 50%; }
.locked .xhair-h, .locked .xhair-v { background: rgba(255,0,0,0.7); }
.xhair-dot {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #ff3333;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);
}

@keyframes xhScan {
  0%,100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 1; }
}
@keyframes xhLock {
  0% { transform: scale(1.3); }
  50% { transform: scale(0.85); }
  100% { transform: scale(1); }
}

.cell-name-spin {
  position: absolute;
  bottom: 4px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 800;
  color: white;
  text-shadow: 0 1px 6px rgba(0,0,0,0.9);
  padding: 4px;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  transition: all 0.15s;
}
.name-active {
  color: #ff4444;
  font-size: 0.85rem;
  text-shadow: 0 0 10px rgba(255,0,0,0.5);
}

/* ─── Result Grid ─── */
.result-fullscreen {
  width: 100%;
  color: white;
}
.video-grid-result {
  display: grid;
  gap: 0.5rem;
}
.result-actions {
  margin-top: 1rem;
}
.video-cell-result {
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 1rem;
  overflow: hidden;
  background: #0a0a14;
}
.cell-loser {
  border: 4px solid #ff0000;
  box-shadow: 0 0 30px rgba(255,0,0,0.4);
  animation: loserShake 0.5s ease-out;
}
.cell-winner {
  border: 3px solid #22c55e;
  box-shadow: 0 0 20px rgba(34,197,94,0.2);
}

@keyframes loserShake {
  0%,100% { transform: translate(0); }
  10% { transform: translate(-4px, 2px); }
  20% { transform: translate(4px, -2px); }
  30% { transform: translate(-3px, 1px); }
  40% { transform: translate(3px, -1px); }
  50% { transform: translate(-2px, 2px); }
}

/* Loser overlay */
.loser-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
}
.blood-splat {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(180,0,0,0.8) 0%, rgba(120,0,0,0.4) 40%, transparent 70%);
  animation: splatIn 0.4s ease-out forwards;
  opacity: 0;
}
.b1 { width: 70%; height: 50%; top: 5%; left: 10%; animation-delay: 0.1s; }
.b2 { width: 50%; height: 60%; top: 25%; right: 5%; animation-delay: 0.2s; }
.b3 { width: 80%; height: 40%; bottom: 15%; left: -5%; animation-delay: 0.3s; }

@keyframes splatIn {
  0% { transform: scale(0.2); opacity: 0; }
  60% { transform: scale(1.2); opacity: 0.9; }
  100% { transform: scale(1); opacity: 1; }
}

.red-vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, transparent 30%, rgba(180,0,0,0.6) 100%);
  animation: vigFade 1s ease-out forwards;
  opacity: 0;
}
@keyframes vigFade { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0.7; } }

.headshot-stamp {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.hs-icon {
  font-size: 2.5rem;
  animation: hsIcon 0.5s ease-out;
  filter: drop-shadow(0 0 15px rgba(255,0,0,0.8));
}
.hs-text {
  font-size: 1.5rem;
  font-weight: 900;
  color: #ff0000;
  text-shadow: 0 0 20px rgba(255,0,0,0.8), 0 2px 10px rgba(0,0,0,0.8);
  letter-spacing: 0.08em;
  animation: hsSlam 0.4s 0.1s ease-out both;
}

@keyframes hsIcon {
  0% { transform: scale(3) rotate(-15deg); opacity: 0; }
  50% { transform: scale(0.9) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); }
}
@keyframes hsSlam {
  0% { transform: scale(3); opacity: 0; }
  60% { transform: scale(0.85); opacity: 1; }
  100% { transform: scale(1); }
}

/* Winner overlay */
.winner-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
}
.shield-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.15) 100%);
}

.cell-name-result {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 900;
  padding: 6px 4px;
  z-index: 15;
}
.name-loser {
  color: #ff4444;
  background: rgba(0,0,0,0.7);
  text-shadow: 0 0 10px rgba(255,0,0,0.5);
}
.name-winner {
  color: #22c55e;
  background: rgba(0,0,0,0.5);
}

/* ─── Classic result animations ─── */
@keyframes resultLoser {
  0% { transform: scale(0.5); opacity: 0; }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes resultWinner {
  0% { transform: scale(0.8); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
.animate-result-loser { animation: resultLoser 0.5s ease-out; }
.animate-result-winner { animation: resultWinner 0.4s ease-out; }
</style>
